import React, { useEffect, useState } from "react";
import {
  FadingView,
  Header,
  type ScrollHeaderProps,
} from "@codeherence/react-native-header";
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Animated, {
  interpolate,
  useAnimatedStyle,
} from "react-native-reanimated";
import { Image } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";

import type { UserProfile } from "@/schemas/user/profile";
import { formatNumberForDisplay } from "@/formatting/number";
import type { AppUserSession } from "@/schemas/auth/session";
import { themes } from "@/Components/Styling/themeConfigs";
import { Spacings } from "@/styling/spacings";
import BackButton from "@/Components/Headers/Components/BackButton";
import { HeaderOptions } from "@/Components/Headers/Components/HeaderOptions";
import UserAvatar from "@/Components/Media/User/Images/UserAvatar";

const ROOT_HORIZONTAL_PADDING = 12;

type Props = {
  profile: UserProfile;
  session: AppUserSession;
  onPressHeader?: () => void;
  largeHeaderHeight: number;
};

// adopted from react-native-header twitter profile example
// https://github.com/codeherence/react-native-header/blob/fa98bfb0a19f8ed27431c056caece0336ffe48ac/example/src/screens/usage/TwitterProfile.tsx#L50-L231
const ProfileHeader = ({
  showNavBar,
  scrollY,
  profile,
  session,
  onPressHeader,
  largeHeaderHeight,
}: ScrollHeaderProps & Props): React.JSX.Element => {
  const [isBlocked, setIsBlocked] = useState<boolean>(profile.isBlocked);
  useEffect(() => {
    setIsBlocked(profile.isBlocked);
  }, [profile.isBlocked, profile.username]);

  const { left, right } = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const [bannerHeight, setBannerHeight] = useState(48);
  const bannerTranslationStyle = useAnimatedStyle(() => {
    const bannerTranslation = interpolate(
      scrollY.value,
      [0, largeHeaderHeight],
      [0, -largeHeaderHeight],
      "clamp",
    );

    return { transform: [{ translateY: bannerTranslation }] };
  });

  const animatedScaleStyle = useAnimatedStyle(() => {
    const bannerHeightRatio = height / bannerHeight;

    const scaleY = interpolate(
      scrollY.value,
      [0, -(height + bannerHeight)],
      [1, bannerHeightRatio],
      "clamp",
    );

    return {
      transform: [{ scaleY }, { scaleX: scaleY }],
    };
  }, [height, bannerHeight]);

  return (
    <View style={styles.container}>
      {/* background image */}
      <Animated.View style={[StyleSheet.absoluteFill, bannerTranslationStyle]}>
        <Animated.View
          onLayout={(e) => setBannerHeight(e.nativeEvent.layout.height)}
          style={animatedScaleStyle}
        >
          <View style={{ marginBottom: -largeHeaderHeight }}>
            <Animated.View
              style={[StyleSheet.absoluteFill, styles.darkOverlay]}
            />

            {profile.background ? (
              <Image
                source={profile.background.attributes.previewUrl}
                contentFit="cover"
                contentPosition="center"
                style={[
                  styles.imageStyle,
                  { width },
                  Platform.OS === "web" && { height: bannerHeight },
                ]}
              />
            ) : (
              <LinearGradient
                colors={["#4c669f", "#3b5998", "#192f6a", "#192f6a", "#0f1d42"]}
                locations={[0, 0.25, 0.5, 0.75, 1]}
                style={[
                  styles.imageStyle,
                  { width },
                  Platform.OS === "web" && { height: bannerHeight },
                ]}
              />
            )}
          </View>
        </Animated.View>
      </Animated.View>

      {/* header */}
      <Header
        showNavBar={showNavBar}
        headerStyle={styles.headerStyle}
        noBottomBorder
        // header left
        headerLeft={
          <>
            <BackButton
              style={styles.headerButtonStyle}
              iconColor="rgba(255, 255, 255, 0.95)"
            />
            <Pressable
              onPress={onPressHeader}
              hitSlop={{ right: 8 }}
              style={{ flex: 1 }}
            >
              <FadingView
                opacity={showNavBar}
                style={styles.headerLeftProfileInfoContainer}
              >
                <UserAvatar
                  avatar={profile.avatar}
                  defaultAvatarColor={"white"}
                  size={36}
                />
                <View style={styles.headerLeftProfileTextContainer}>
                  <Text style={styles.navBarTitle} numberOfLines={1}>
                    @{profile.username}
                  </Text>
                  <Text style={styles.graySmallText}>
                    {formatNumberForDisplay(
                      profile.privatePhotoCount + profile.publicImages.total,
                    )}{" "}
                    Images
                  </Text>
                </View>
              </FadingView>
            </Pressable>
          </>
        }
        headerLeftStyle={[
          styles.headerLeftContainer,
          { paddingLeft: Math.max(left, ROOT_HORIZONTAL_PADDING) },
        ]}
        // header right
        headerRight={
          <HeaderOptions
            session={session}
            target={profile.username}
            isBlocked={isBlocked}
            onHandleBlockToggle={(newStatus) => {
              setIsBlocked(newStatus);
            }}
            style={styles.headerButtonStyle}
            iconColor="rgba(255, 255, 255, 0.95)"
          />
        }
        headerRightStyle={[
          styles.headerRightContainer,
          { paddingRight: Math.max(right, ROOT_HORIZONTAL_PADDING) },
        ]}
      />
    </View>
  );
};

const currentTheme = themes.morning;

const styles = StyleSheet.create({
  container: {
    position: "relative",
  },
  darkOverlay: {
    zIndex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
  },
  imageStyle: {
    height: "100%",
  },
  headerStyle: {
    backgroundColor: "transparent",
  },
  headerLeftContainer: {
    gap: Spacings.s4,
    width: "80%",
  },
  headerLeftProfileInfoContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacings.s2,
  },
  headerLeftProfileTextContainer: {
    flex: 1,
    flexDirection: "column",
  },
  navBarTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "rgba(255, 255, 255, 0.95)",
  },
  graySmallText: {
    color: "rgba(230, 230, 230, 0.8)",
    fontSize: currentTheme.typography.body.size.mobile,
  },
  headerRightContainer: {
    paddingLeft: Spacings.s3,
    width: "20%",
  },
  headerButtonStyle: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    borderRadius: 100,
    padding: 8,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 99,
  },
});

export default ProfileHeader;
